import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { getSurplusList, getSurplusTypes, getCategories } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';
import MobileBottomNav from '@/components/layout/MobileBottomNav';
import TexHubLogo from '@/components/brand/TexHubLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { 
  Search, Plus, MapPin, Building2, Package, Clock, Filter, X,
  ImageIcon, CheckCircle2, Tag, Zap, TrendingUp, ArrowLeft,
  Flame, Star, Eye, ChevronRight, Sparkles, Timer
} from 'lucide-react';

const SurplusList = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isUSA, formatPrice } = useLocale();
  const [surplus, setSurplus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surplusTypes, setSurplusTypes] = useState([]);
  const [categories, setCategories] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [filters, setFilters] = useState({
    surplus_type: '',
    category: '',
    location: '',
    min_price: '',
    max_price: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [surplusData, typesData, categoriesData] = await Promise.all([
        getSurplusList(),
        getSurplusTypes(isUSA ? 'en' : 'es'),
        getCategories(isUSA ? 'en' : 'es')
      ]);
      setSurplus(surplusData);
      setSurplusTypes(typesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [isUSA]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyFilters = async (typeFilter = selectedType) => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter && typeFilter !== 'all') params.surplus_type = typeFilter;
      if (filters.category && filters.category !== 'all') params.category = filters.category;
      if (filters.location) params.location = filters.location;
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;

      const data = await getSurplusList(params);
      setSurplus(data);
      setShowFilters(false);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    applyFilters(type);
  };

  const clearFilters = () => {
    setFilters({
      surplus_type: '',
      category: '',
      location: '',
      min_price: '',
      max_price: ''
    });
    setSelectedType('all');
    fetchData();
  };

  // Filter by search
  const filteredSurplus = surplus.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.company_name?.toLowerCase().includes(query) ||
      item.location?.toLowerCase().includes(query)
    );
  });

  // Type badge styles - Simplified to 2 categories
  const getTypeBadge = (type, label) => {
    const styles = {
      saldo_tela: { bg: 'bg-gradient-to-r from-amber-500 to-orange-500', icon: <Tag className="w-3 h-3" /> },
      saldo_ropa: { bg: 'bg-gradient-to-r from-purple-500 to-pink-500', icon: <Package className="w-3 h-3" /> }
    };
    const style = styles[type] || styles.saldo_tela;
    return (
      <span className={`${style.bg} text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg`}>
        {style.icon}
        {label || type}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      disponible: { bg: 'bg-emerald-500', text: isUSA ? 'Available' : 'Disponible', icon: <CheckCircle2 className="w-3 h-3" /> },
      reservado: { bg: 'bg-amber-500', text: isUSA ? 'Reserved' : 'Reservado', icon: <Clock className="w-3 h-3" /> },
      vendido: { bg: 'bg-slate-500', text: isUSA ? 'Sold' : 'Vendido', icon: <CheckCircle2 className="w-3 h-3" /> }
    };
    const style = styles[status] || styles.disponible;
    return (
      <span className={`${style.bg} text-white text-xs px-2 py-1 rounded-full flex items-center gap-1`}>
        {style.icon}
        {style.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="flex items-center gap-2">
              <TexHubLogo className="h-8" />
            </Link>
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Link to="/dashboard/surplus/new">
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold">
                    <Plus className="w-4 h-4 mr-2" />
                    {isUSA ? 'Post Surplus' : 'Publicar Saldo'}
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    {isUSA ? 'Login to post' : 'Ingresar para publicar'}
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Hero Content */}
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 px-4 py-2 rounded-full mb-6">
              <Flame className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 font-medium">
                {isUSA ? 'Hot deals updated daily' : 'Ofertas actualizadas diariamente'}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              {isUSA ? 'Surplus & Deals' : 'Saldos y Remates'}
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              {isUSA 
                ? 'Find exclusive deals on fabric surplus, overstocks and liquidations'
                : 'Encuentra ofertas exclusivas en saldos de tela, excedentes y liquidaciones'
              }
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isUSA ? 'Search surplus, fabrics, deals...' : 'Buscar saldos, telas, ofertas...'}
                className="pl-12 pr-4 h-14 text-lg bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-xl focus:bg-white/20"
                data-testid="surplus-search-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Type Filter Tabs */}
      <div className="bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 py-3 overflow-x-auto hide-scrollbar">
            <button
              onClick={() => handleTypeChange('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedType === 'all' 
                  ? 'bg-slate-900 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {isUSA ? 'All' : 'Todos'}
            </button>
            {surplusTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => handleTypeChange(type.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                  selectedType === type.value 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {type.value === 'saldo_tela' && <Tag className="w-4 h-4" />}
                {type.value === 'saldo_ropa' && <Package className="w-4 h-4" />}
                {type.label}
              </button>
            ))}
            
            {/* More Filters Button */}
            <Sheet open={showFilters} onOpenChange={setShowFilters}>
              <SheetTrigger asChild>
                <button className="px-4 py-2 rounded-full text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  {isUSA ? 'More Filters' : 'Más Filtros'}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle>{isUSA ? 'Filters' : 'Filtros'}</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-6">
                  {/* Category */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      {isUSA ? 'Category' : 'Categoría'}
                    </label>
                    <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'All categories' : 'Todas las categorías'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isUSA ? 'All' : 'Todas'}</SelectItem>
                        {Object.entries(categories).map(([key, value]) => (
                          <SelectItem key={key} value={key}>{value}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Location */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      {isUSA ? 'Location' : 'Ubicación'}
                    </label>
                    <Input
                      value={filters.location}
                      onChange={(e) => setFilters({...filters, location: e.target.value})}
                      placeholder={isUSA ? 'City or state' : 'Ciudad o estado'}
                    />
                  </div>

                  {/* Price Range */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      {isUSA ? 'Price Range' : 'Rango de Precio'}
                    </label>
                    <div className="flex gap-3">
                      <Input
                        type="number"
                        value={filters.min_price}
                        onChange={(e) => setFilters({...filters, min_price: e.target.value})}
                        placeholder="Min"
                      />
                      <Input
                        type="number"
                        value={filters.max_price}
                        onChange={(e) => setFilters({...filters, max_price: e.target.value})}
                        placeholder="Max"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" className="flex-1" onClick={clearFilters}>
                      {isUSA ? 'Clear' : 'Limpiar'}
                    </Button>
                    <Button 
                      className="flex-1 bg-amber-500 hover:bg-amber-600" 
                      onClick={() => applyFilters()}
                    >
                      {isUSA ? 'Apply Filters' : 'Aplicar Filtros'}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Bar */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-600">
            <span className="font-semibold text-slate-900">{filteredSurplus.length}</span>
            {isUSA ? ' deals available' : ' ofertas disponibles'}
          </p>
          {(selectedType !== 'all' || searchQuery) && (
            <button 
              onClick={clearFilters}
              className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              {isUSA ? 'Clear filters' : 'Limpiar filtros'}
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
                <div className="aspect-[4/3] bg-slate-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-slate-200 rounded w-2/3" />
                  <div className="h-3 bg-slate-200 rounded w-1/2" />
                  <div className="h-6 bg-slate-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredSurplus.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">
              {isUSA ? 'No surplus found' : 'No se encontraron saldos'}
            </h3>
            <p className="text-slate-500 mb-6">
              {isUSA ? 'Try adjusting your filters or search' : 'Intenta ajustar tus filtros o búsqueda'}
            </p>
            <Button onClick={clearFilters} variant="outline">
              {isUSA ? 'Clear filters' : 'Limpiar filtros'}
            </Button>
          </div>
        ) : (
          /* Product Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredSurplus.map((item, index) => (
              <Link
                key={item.surplus_id}
                to={`/surplus/${item.surplus_id}`}
                className="group"
                data-testid={`surplus-card-${item.surplus_id}`}
              >
                <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 bg-white rounded-2xl border-0 shadow-sm group-hover:-translate-y-1">
                  {/* Image */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                    {item.images && item.images.length > 0 ? (
                      <img 
                        src={getFirstImage(item.images)} 
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-16 h-16 text-slate-300" />
                      </div>
                    )}
                    
                    {/* Type Badge - Top Left */}
                    <div className="absolute top-3 left-3">
                      {getTypeBadge(item.surplus_type, item.surplus_type_label)}
                    </div>
                    
                    {/* Status Badge - Top Right */}
                    <div className="absolute top-3 right-3">
                      {getStatusBadge(item.status)}
                    </div>

                    {/* Urgency indicator for limited items */}
                    {item.quantity && item.quantity < 100 && (
                      <div className="absolute bottom-3 left-3 bg-red-500/90 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Timer className="w-3 h-3" />
                        {isUSA ? 'Limited' : 'Limitado'}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <CardContent className="p-4">
                    {/* Company */}
                    {item.company_name && (
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
                        <Building2 className="w-3.5 h-3.5" />
                        <span className="truncate">{item.company_name}</span>
                        {item.is_verified && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        )}
                      </div>
                    )}

                    {/* Title */}
                    <h3 className="font-semibold text-slate-900 line-clamp-2 mb-2 group-hover:text-amber-600 transition-colors">
                      {item.name}
                    </h3>

                    {/* Location */}
                    {item.location && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{item.location}</span>
                      </div>
                    )}

                    {/* Price & Quantity */}
                    <div className="flex items-end justify-between pt-3 border-t border-slate-100">
                      <div>
                        {item.price ? (
                          <div className="text-2xl font-bold text-slate-900">
                            ${item.price.toLocaleString(isUSA ? 'en-US' : 'es-MX')}
                            {item.unit && (
                              <span className="text-sm font-normal text-slate-500">/{item.unit}</span>
                            )}
                          </div>
                        ) : (
                          <div className="text-lg font-semibold text-amber-600">
                            {isUSA ? 'Request quote' : 'Cotizar'}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        {item.quantity && (
                          <div className="text-sm text-slate-500">
                            {item.quantity.toLocaleString()} {item.unit}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Eye className="w-3 h-3" />
                          {item.views_count || 0}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* CTA Banner */}
        {!isAuthenticated && (
          <div className="mt-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 text-center text-white">
            <Sparkles className="w-10 h-10 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">
              {isUSA ? 'Have surplus to sell?' : '¿Tienes saldos para vender?'}
            </h3>
            <p className="text-white/90 mb-6 max-w-lg mx-auto">
              {isUSA 
                ? 'Post your surplus for free and reach thousands of potential buyers'
                : 'Publica tus saldos gratis y llega a miles de compradores potenciales'
              }
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 font-semibold">
                {isUSA ? 'Start selling' : 'Empezar a vender'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Mobile Bottom Nav */}
      <MobileBottomNav />

      {/* Custom Scrollbar Style */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default SurplusList;
